import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Autocomplete,
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
  Chip,
  Button,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  MenuItem,
  Select,
  Tooltip,
  Divider,
  Stack,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  AttachMoney as MoneyIcon,
  Restaurant as FeedIcon,
  Egg as EggIcon,
  Notes as NotesIcon,
  Assessment as StatsIcon,
  Edit as EditIcon,
  GridView as BlocksIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Scale as ScaleIcon,
} from '@mui/icons-material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import {
  useBird,
  useBirdLogs,
  useBirds,
  useFeedEstimate,
  useBirdBlocks,
  useBirdBlocksFeed,
  useCreateBirdBlocks,
  useUpdateBirdBlock,
  useDeleteBirdBlock,
  useUpdateBird,
} from '../hooks/useBirds';
import { Bird, BirdBlock } from '../types';
import { useBirdOverview } from '../hooks/useDashboard';
import { useFeedAssignments, useUpdateFeedAssignment } from '../hooks/useFeed';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`bird-tabpanel-${index}`}
      aria-labelledby={`bird-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

const BirdDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);
  const tabInitialized = useRef(false);

  const birdId = parseInt(id || '0');

  const { data: allBirdsData } = useBirds({ per_page: 200 });
  const allBirds = allBirdsData?.data ?? [];
  const [editConsumptionOpen, setEditConsumptionOpen] = useState(false);
  const [newDailyConsumption, setNewDailyConsumption] = useState('');

  // Cage date state
  const [editCageDateOpen, setEditCageDateOpen] = useState(false);
  const [cageDateInput, setCageDateInput] = useState('');

  // Rearing blocks state
  const [editBlockOpen, setEditBlockOpen] = useState(false);
  const [editingBlock, setEditingBlock] = useState<BirdBlock | null>(null);
  const [editBlockName, setEditBlockName] = useState('');
  const [editBlockNotes, setEditBlockNotes] = useState('');
  const [addBlockOpen, setAddBlockOpen] = useState(false);
  const [newBlockRows, setNewBlockRows] = useState([{ name: '', quantity: '', type: 'rearing' }]);
  const [editBlockFeeders, setEditBlockFeeders] = useState('');
  const [editBlockDrinkers, setEditBlockDrinkers] = useState('');
  const [editBlockInitialQty, setEditBlockInitialQty] = useState('');

  // Data fetching
  const {
    data: bird,
    isLoading: birdLoading,
    isFetching: birdFetching,
    error: birdError,
    refetch: refetchBird,
  } = useBird(birdId);
  const {
    data: birdOverview,
    isFetching: overviewFetching,
    isLoading: overviewLoading,
    error: overviewError,
    refetch: refetchOverview,
  } = useBirdOverview(birdId);
  const { data: birdLogs, isLoading: logsLoading } = useBirdLogs(birdId);
  const { data: assignmentsData } = useFeedAssignments({
    birds_id: birdId,
    is_active: true,
    per_page: 1,
  });
  const activeAssignment = assignmentsData?.data?.[0] ?? null;
  const updateAssignment = useUpdateFeedAssignment();
  const { data: feedEstimate } = useFeedEstimate(birdId);
  const { data: blocks, isLoading: blocksLoading } = useBirdBlocks(birdId);
  const isCaged = !!bird?.cage_date;
  const blockType = isCaged ? 'cage' : 'rearing';
  const visibleBlocks = blocks?.filter((b: any) => (b.type ?? 'rearing') === blockType);
  const { data: blocksFeed } = useBirdBlocksFeed(birdId, !!(blocks && blocks.length > 0));
  const createBlocks = useCreateBirdBlocks(birdId);
  const updateBlock = useUpdateBirdBlock(birdId);
  const deleteBlock = useDeleteBirdBlock(birdId);
  const updateBird = useUpdateBird();

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Default to Rearing Blocks tab (index 5) for non-laying birds
  useEffect(() => {
    if (bird && !tabInitialized.current) {
      tabInitialized.current = true;
      if (!bird.is_laying) setTabValue(5);
    }
  }, [bird]);

  const hasError = birdError || overviewError;

  if (birdLoading || overviewLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (hasError || !bird) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <Box textAlign="center" maxWidth="500px">
          <Alert severity="error" sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Failed to Load Bird Details
            </Typography>
            <Typography variant="body2" paragraph>
              {birdError instanceof Error
                ? birdError.message
                : overviewError instanceof Error
                  ? overviewError.message
                  : 'An error occurred while fetching bird details. Please try again.'}
            </Typography>
          </Alert>
          <Button
            variant="contained"
            onClick={() => {
              refetchBird();
              refetchOverview();
            }}
            sx={{ color: '#fff' }}
          >
            Retry
          </Button>
        </Box>
      </Box>
    );
  }

  // Stats overview from API response
  const birdInfo = birdOverview?.bird_info || {};
  const mortalityOverview = birdOverview?.mortality_overview || {};
  const expenditureOverview = birdOverview?.expenditure_overview || {};
  const productionOverview = birdOverview?.production_overview || {};
  const financialOverview = birdOverview?.financial_overview || {};
  const performanceMetrics = birdOverview?.performance_metrics || {};
  const alerts = birdOverview?.alerts || [];

  // Feed logs
  const feedLogs = birdLogs?.filter((log: any) => log.type === 'feed') || [];

  // Expenditure columns (using recent_expenditures from overview)
  const expenditureColumns: GridColDef[] = [
    { field: 'id', headerName: 'ID', width: 70 },
    { field: 'expense_date', headerName: 'Date', flex: 1, minWidth: 120 },
    {
      field: 'category',
      headerName: 'Category',
      flex: 1,
      minWidth: 150,
      renderCell: (params: GridRenderCellParams) => (
        <Chip label={params.row.category?.name || 'N/A'} size="small" />
      ),
    },
    { field: 'title', headerName: 'Description', flex: 1.5, minWidth: 200 },
    {
      field: 'total_amount',
      headerName: 'Amount',
      flex: 0.8,
      minWidth: 120,
      renderCell: (params: GridRenderCellParams) => params.value,
    },
    {
      field: 'amount_paid',
      headerName: 'Paid',
      flex: 0.8,
      minWidth: 120,
      renderCell: (params: GridRenderCellParams) => params.value,
    },
    {
      field: 'amount_left',
      headerName: 'Outstanding',
      flex: 0.8,
      minWidth: 120,
      renderCell: (params: GridRenderCellParams) => (
        <Typography
          variant="body2"
          fontWeight={600}
          color={params.value === 'GH₵ 0.00' ? 'success.main' : 'error.main'}
        >
          {params.value}
        </Typography>
      ),
    },
  ];

  // Egg production columns (using recent_production from overview)
  const eggProductionColumns: GridColDef[] = [
    { field: 'id', headerName: 'ID', width: 70 },
    {
      field: 'production_date',
      headerName: 'Date',
      flex: 1,
      minWidth: 120,
      renderCell: (params: GridRenderCellParams) => new Date(params.value).toLocaleDateString(),
    },
    {
      field: 'total_quantity',
      headerName: 'Total Eggs',
      flex: 0.8,
      minWidth: 110,
      type: 'number',
    },
    {
      field: 'crate_quantity',
      headerName: 'Crates',
      flex: 0.7,
      minWidth: 90,
      type: 'number',
    },
    {
      field: 'broken_quantity',
      headerName: 'Broken',
      flex: 0.7,
      minWidth: 90,
      type: 'number',
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2" color="error.main" fontWeight={600}>
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'lay_percentage',
      headerName: 'Rate %',
      flex: 0.7,
      minWidth: 90,
      renderCell: (params: GridRenderCellParams) => `${params.value}%`,
    },
    {
      field: 'birds_quantity',
      headerName: 'Bird Count',
      flex: 0.8,
      minWidth: 110,
      type: 'number',
    },
  ];

  // Feed consumption columns
  const feedColumns: GridColDef[] = [
    { field: 'id', headerName: 'ID', width: 70 },
    {
      field: 'administered_at',
      headerName: 'Date',
      flex: 1,
      minWidth: 140,
      renderCell: (params: GridRenderCellParams) => new Date(params.value).toLocaleString(),
    },
    {
      field: 'product_name',
      headerName: 'Feed Product',
      flex: 1.5,
      minWidth: 200,
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2" fontWeight={600}>
          {params.value || 'N/A'}
        </Typography>
      ),
    },
    {
      field: 'quantity',
      headerName: 'Quantity',
      flex: 0.8,
      minWidth: 120,
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2">
          {params.value} {params.row.unit}
        </Typography>
      ),
    },
    {
      field: 'cost_per_kg',
      headerName: 'Cost/kg',
      flex: 0.8,
      minWidth: 100,
      renderCell: (params: GridRenderCellParams) =>
        `GH₵ ${parseFloat(params.value || 0).toFixed(2)}`,
    },
    {
      field: 'total_feed_cost',
      headerName: 'Total Cost',
      flex: 0.8,
      minWidth: 120,
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2" fontWeight={600} color="primary">
          GH₵ {parseFloat(params.value || 0).toLocaleString()}
        </Typography>
      ),
    },
    {
      field: 'administrator',
      headerName: 'Administered By',
      flex: 1,
      minWidth: 150,
      renderCell: (params: GridRenderCellParams) => params.row.administrator?.name || 'N/A',
    },
  ];

  // Other logs columns
  const logsColumns: GridColDef[] = [
    { field: 'id', headerName: 'ID', width: 70 },
    {
      field: 'administered_at',
      headerName: 'Date',
      flex: 1,
      minWidth: 140,
      renderCell: (params: GridRenderCellParams) => new Date(params.value).toLocaleString(),
    },
    {
      field: 'type',
      headerName: 'Type',
      flex: 0.8,
      minWidth: 100,
      renderCell: (params: GridRenderCellParams) => (
        <Chip label={params.value} size="small" color="primary" variant="outlined" />
      ),
    },
    {
      field: 'product_name',
      headerName: 'Product/Activity',
      flex: 1.5,
      minWidth: 200,
      renderCell: (params: GridRenderCellParams) => params.value || 'N/A',
    },
    {
      field: 'quantity',
      headerName: 'Quantity',
      flex: 0.8,
      minWidth: 100,
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2">
          {params.value} {params.row.unit || ''}
        </Typography>
      ),
    },
    {
      field: 'notes',
      headerName: 'Notes',
      flex: 1.2,
      minWidth: 150,
      renderCell: (params: GridRenderCellParams) => params.value || '-',
    },
    {
      field: 'administrator',
      headerName: 'By',
      flex: 1,
      minWidth: 150,
      renderCell: (params: GridRenderCellParams) => params.row.administrator?.name || 'N/A',
    },
  ];

  const nonFeedLogs = birdLogs?.filter((log: any) => log.type !== 'feed') || [];

  const handleOpenEditConsumption = () => {
    setNewDailyConsumption(activeAssignment?.daily_consumption_kg?.toString() ?? '');
    setEditConsumptionOpen(true);
  };

  const handleSaveConsumption = () => {
    if (!activeAssignment) return;
    const value = parseFloat(newDailyConsumption);
    if (isNaN(value) || value <= 0) return;
    updateAssignment.mutate(
      { id: activeAssignment.id, data: { daily_consumption_kg: value } },
      { onSuccess: () => setEditConsumptionOpen(false) }
    );
  };

  const handleOpenEditBlock = (block: BirdBlock) => {
    setEditingBlock(block);
    setEditBlockName(block.name);
    setEditBlockNotes(block.notes ?? '');
    setEditBlockFeeders(String(block.feeders ?? ''));
    setEditBlockDrinkers(String(block.drinkers ?? ''));
    setEditBlockInitialQty(String(block.initial_quantity ?? ''));
    setEditBlockOpen(true);
  };

  const handleSaveEditBlock = () => {
    if (!editingBlock) return;
    updateBlock.mutate(
      {
        blockId: editingBlock.id,
        data: {
          name: editBlockName,
          notes: editBlockNotes,
          feeders: editBlockFeeders !== '' ? Number(editBlockFeeders) : undefined,
          drinkers: editBlockDrinkers !== '' ? Number(editBlockDrinkers) : undefined,
          initial_quantity: editBlockInitialQty !== '' ? Number(editBlockInitialQty) : undefined,
        },
      },
      { onSuccess: () => setEditBlockOpen(false) }
    );
  };

  const handleAddBlocksSave = () => {
    const validBlocks = newBlockRows.filter((r) => r.name.trim() && parseInt(r.quantity) > 0);
    if (validBlocks.length === 0) return;
    createBlocks.mutate(
      {
        blocks: validBlocks.map((r) => ({
          name: r.name.trim(),
          type: r.type as 'rearing' | 'cage',
          quantity: parseInt(r.quantity),
        })),
      },
      {
        onSuccess: () => {
          setAddBlockOpen(false);
          setNewBlockRows([{ name: '', quantity: '', type: blockType }]);
        },
      }
    );
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {(birdFetching || overviewFetching) && (
        <LinearProgress
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 9999,
            height: 3,
            '& .MuiLinearProgress-bar': {
              background: 'linear-gradient(90deg, #ff9800 0%, #ffa726 100%)',
            },
          }}
        />
      )}
      {/* Page Header */}
      <Box
        mb={3}
        display="flex"
        alignItems="flex-start"
        justifyContent="space-between"
        gap={2}
        flexWrap="wrap"
      >
        <Box>
          <Typography variant="h4" fontWeight={700} gutterBottom>
            {bird.name ?? `Bird #${birdId}`}
          </Typography>
          <Box display="flex" gap={1} flexWrap="wrap">
            {bird.birds_type && (
              <Chip label={bird.birds_type} size="small" variant="outlined" color="primary" />
            )}
            {bird.color && <Chip label={bird.color} size="small" variant="outlined" />}
            {bird.status && (
              <Chip label={bird.status.replaceAll('_', ' ')} size="small" color="info" />
            )}
            {bird.is_laying && <Chip label="Laying" size="small" color="success" />}
          </Box>
        </Box>
        <Autocomplete
          options={allBirds}
          getOptionLabel={(b: Bird) => b.name ?? `Bird #${b.id}`}
          value={allBirds.find((b: Bird) => b.id === birdId) ?? null}
          onChange={(_e: React.SyntheticEvent, selected: Bird | null) => {
            if (selected) navigate(`/birds/${selected.id}`);
          }}
          isOptionEqualToValue={(a: Bird, b: Bird) => a.id === b.id}
          sx={{ minWidth: 240 }}
          renderInput={(params) => <TextField {...params} label="Switch bird" size="small" />}
          renderOption={(props: React.HTMLAttributes<HTMLLIElement>, option: Bird) => (
            <li {...props} key={option.id}>
              <Box>
                <Typography variant="body2" fontWeight={600}>
                  {option.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {option.birds_type} · {option.color}
                </Typography>
              </Box>
            </li>
          )}
        />
      </Box>

      {/* Stats Cards */}
      <Box display="flex" gap={2.5} mb={4} flexWrap="wrap">
        <Paper
          sx={{
            p: 3,
            minWidth: 180,
            flex: 1,
            border: '1px solid rgba(0,0,0,0.06)',
          }}
        >
          <Box display="flex" alignItems="center" gap={1} mb={1}>
            <TrendingUpIcon color="primary" />
            <Typography
              variant="caption"
              color="text.secondary"
              fontWeight={600}
              sx={{ letterSpacing: '0.5px' }}
            >
              CURRENT QUANTITY
            </Typography>
          </Box>
          <Typography variant="h4" fontWeight="bold" color="primary">
            {birdInfo.current_quantity?.toLocaleString() ||
              bird.current_quantity?.toLocaleString() ||
              '0'}
          </Typography>
          <Typography variant="caption" color="text.secondary" mt={0.5}>
            Survival: {mortalityOverview.survival_rate?.toFixed(1) || '0'}%
          </Typography>
        </Paper>

        <Paper
          sx={{
            p: 3,
            minWidth: 180,
            flex: 1,
            border: '1px solid rgba(0,0,0,0.06)',
          }}
        >
          <Box display="flex" alignItems="center" gap={1} mb={1}>
            <EggIcon color="success" />
            <Typography
              variant="caption"
              color="text.secondary"
              fontWeight={600}
              sx={{ letterSpacing: '0.5px' }}
            >
              TOTAL EGGS PRODUCED
            </Typography>
          </Box>
          <Typography variant="h4" fontWeight="bold" color="success.main">
            {productionOverview.total_eggs_produced?.toLocaleString() || '0'}
          </Typography>
          <Typography variant="caption" color="text.secondary" mt={0.5}>
            Avg: {productionOverview.average_daily_production?.toFixed(0) || '0'}/day
          </Typography>
        </Paper>

        <Paper
          sx={{
            p: 3,
            minWidth: 180,
            flex: 1,
            border: '1px solid rgba(0,0,0,0.06)',
          }}
        >
          <Box display="flex" alignItems="center" gap={1} mb={1}>
            <MoneyIcon sx={{ color: '#2e7d32' }} />
            <Typography
              variant="caption"
              color="text.secondary"
              fontWeight={600}
              sx={{ letterSpacing: '0.5px' }}
            >
              NET PROFIT
            </Typography>
          </Box>
          <Typography variant="h4" fontWeight="bold" sx={{ color: '#2e7d32' }}>
            GH₵ {(financialOverview.net_profit / 100)?.toLocaleString() || '0'}
          </Typography>
          <Typography variant="caption" color="text.secondary" mt={0.5}>
            Margin: {financialOverview.profit_margin?.toFixed(1) || '0'}%
          </Typography>
        </Paper>

        <Paper
          sx={{
            p: 3,
            minWidth: 180,
            flex: 1,
            border: '1px solid rgba(0,0,0,0.06)',
          }}
        >
          <Box display="flex" alignItems="center" gap={1} mb={1}>
            <MoneyIcon color="secondary" />
            <Typography
              variant="caption"
              color="text.secondary"
              fontWeight={600}
              sx={{ letterSpacing: '0.5px' }}
            >
              TOTAL EXPENSES
            </Typography>
          </Box>
          <Typography variant="h4" fontWeight="bold" color="secondary.main">
            GH₵ {(expenditureOverview.total_expenditures / 100)?.toLocaleString() || '0'}
          </Typography>
          <Typography variant="caption" color="text.secondary" mt={0.5}>
            Per bird: GH₵ {(expenditureOverview.cost_per_bird / 100)?.toFixed(0) || '0'}
          </Typography>
        </Paper>

        <Paper
          sx={{
            p: 3,
            minWidth: 180,
            flex: 1,
            border: '1px solid rgba(0,0,0,0.06)',
          }}
        >
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
            <Box display="flex" alignItems="center" gap={1}>
              <FeedIcon color="primary" />
              <Typography
                variant="caption"
                color="text.secondary"
                fontWeight={600}
                sx={{ letterSpacing: '0.5px' }}
              >
                DAILY FEED
              </Typography>
            </Box>
            {activeAssignment && (
              <Tooltip title="Edit daily consumption">
                <IconButton size="small" onClick={handleOpenEditConsumption}>
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
          {activeAssignment ? (
            <>
              <Typography variant="h4" fontWeight="bold" color="primary">
                {parseFloat(activeAssignment.daily_consumption_kg as any).toFixed(1)} kg
              </Typography>
              <Typography variant="caption" color="text.secondary" mt={0.5}>
                {activeAssignment.feed_type?.name} · {activeAssignment.feed_brand?.name}
              </Typography>
            </>
          ) : (
            <>
              <Typography variant="h4" fontWeight="bold" color="text.disabled">
                —
              </Typography>
              <Typography variant="caption" color="text.secondary" mt={0.5}>
                No active assignment
              </Typography>
            </>
          )}
          {feedEstimate && (
            <Box mt={1} pt={1} sx={{ borderTop: '1px solid rgba(0,0,0,0.08)' }}>
              <Typography variant="caption" color="text.secondary" display="block">
                Estimated need
              </Typography>
              <Typography variant="body2" fontWeight={700} color="text.primary">
                {feedEstimate.total_feed_kg} kg/day
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {feedEstimate.daily_feed_per_bird_g} g/bird · {feedEstimate.bags_required_daily} bag
                {feedEstimate.bags_required_daily !== 1 ? 's' : ''}
                {feedEstimate.remaining_kg_daily > 0 ? ` ${feedEstimate.remaining_kg_daily}kg` : ''}
                /day
              </Typography>
            </Box>
          )}
        </Paper>
      </Box>

      {/* Edit daily consumption dialog */}
      <Dialog
        open={editConsumptionOpen}
        onClose={() => setEditConsumptionOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Set Daily Consumption</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" mb={2}>
            {activeAssignment?.feed_type?.name} · {activeAssignment?.feed_brand?.name}
          </Typography>
          <TextField
            label="Daily consumption (kg)"
            type="number"
            fullWidth
            value={newDailyConsumption}
            onChange={(e) => setNewDailyConsumption(e.target.value)}
            inputProps={{ min: 0.01, step: 0.1 }}
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditConsumptionOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSaveConsumption}
            disabled={
              updateAssignment.isPending ||
              !newDailyConsumption ||
              parseFloat(newDailyConsumption) <= 0
            }
            sx={{ color: '#fff' }}
          >
            {updateAssignment.isPending ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cage Date Dialog */}
      <Dialog
        open={editCageDateOpen}
        onClose={() => setEditCageDateOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Date Transferred to Cage</DialogTitle>
        <DialogContent>
          <TextField
            label="Date"
            type="date"
            fullWidth
            slotProps={{ inputLabel: { shrink: true } }}
            value={cageDateInput}
            onChange={(e) => setCageDateInput(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditCageDateOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => {
              updateBird.mutate(
                { id: birdId, data: { cage_date: cageDateInput || null } },
                { onSuccess: () => setEditCageDateOpen(false) }
              );
            }}
            disabled={updateBird.isPending}
            sx={{ color: '#fff' }}
          >
            {updateBird.isPending ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Tabs */}
      <Paper
        sx={{
          width: '100%',
          border: '1px solid rgba(0,0,0,0.06)',
        }}
      >
        <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
          <Tabs value={tabValue} onChange={handleTabChange} aria-label="bird detail tabs">
            <Tab
              icon={<StatsIcon />}
              iconPosition="start"
              label="Overview"
              sx={{ textTransform: 'none', fontWeight: 600 }}
            />
            <Tab
              icon={<MoneyIcon />}
              iconPosition="start"
              label="Expenses"
              sx={{ textTransform: 'none', fontWeight: 600 }}
            />
            <Tab
              icon={<FeedIcon />}
              iconPosition="start"
              label="Feed Consumption"
              sx={{ textTransform: 'none', fontWeight: 600 }}
            />
            <Tab
              icon={<EggIcon />}
              iconPosition="start"
              label="Egg Production"
              sx={{ textTransform: 'none', fontWeight: 600 }}
            />
            <Tab
              icon={<NotesIcon />}
              iconPosition="start"
              label="Bird Logs"
              sx={{ textTransform: 'none', fontWeight: 600 }}
            />
            <Tab
              icon={<BlocksIcon />}
              iconPosition="start"
              label={isCaged ? 'Cage Blocks' : 'Rearing Blocks'}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            />
          </Tabs>
        </Box>

        {/* Overview Tab */}
        <TabPanel value={tabValue} index={0}>
          <Box px={2}>
            <Typography variant="h6" fontWeight="bold" mb={3}>
              Bird Statistics & Performance
            </Typography>

            <Box display="flex" flexWrap="wrap" gap={3}>
              {/* Basic Info */}
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  flex: 1,
                  minWidth: 280,
                  background:
                    'linear-gradient(135deg, rgba(131, 65, 5, 0.05) 0%, rgba(255, 152, 0, 0.05) 100%)',
                  border: '1px solid rgba(131, 65, 5, 0.1)',
                  borderRadius: 2,
                }}
              >
                <Typography variant="subtitle2" fontWeight="bold" color="primary" mb={2}>
                  BASIC INFORMATION
                </Typography>
                <Box display="flex" flexDirection="column" gap={1.5}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Procurement ID
                    </Typography>
                    <Typography variant="body1" fontWeight={600}>
                      {bird.procurement_id}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Bird Type
                    </Typography>
                    <Typography variant="body1" fontWeight={600}>
                      {bird.birds_type}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Color
                    </Typography>
                    <Typography variant="body1" fontWeight={600}>
                      {bird.color}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Age (weeks)
                    </Typography>
                    <Typography variant="body1" fontWeight={600}>
                      {bird.age_weeks}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Date transferred to cage
                    </Typography>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography variant="body1" fontWeight={600}>
                        {bird.cage_date
                          ? new Date(bird.cage_date).toLocaleDateString('en-GB', {
                              day: 'numeric',
                              month: 'short',
                              year: 'numeric',
                            })
                          : '—'}
                      </Typography>
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setCageDateInput(bird.cage_date ?? '');
                            setEditCageDateOpen(true);
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                </Box>
              </Paper>

              {/* Production Stats */}
              {bird.is_laying && (
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    flex: 1,
                    minWidth: 280,
                    background:
                      'linear-gradient(135deg, rgba(76, 175, 80, 0.05) 0%, rgba(139, 195, 74, 0.05) 100%)',
                    border: '1px solid rgba(76, 175, 80, 0.2)',
                    borderRadius: 2,
                  }}
                >
                  <Typography variant="subtitle2" fontWeight="bold" color="success.main" mb={2}>
                    EGG PRODUCTION STATS
                  </Typography>
                  <Box display="flex" flexDirection="column" gap={1.5}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Total Eggs
                      </Typography>
                      <Typography variant="body1" fontWeight={600}>
                        {productionOverview.total_eggs_produced?.toLocaleString() || '0'}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Good Eggs
                      </Typography>
                      <Typography variant="body1" fontWeight={600} color="success.main">
                        {productionOverview.total_good_eggs?.toLocaleString() || '0'}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Broken Eggs
                      </Typography>
                      <Typography variant="body1" fontWeight={600} color="error.main">
                        {productionOverview.total_broken_eggs?.toLocaleString() || '0'}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Average Daily
                      </Typography>
                      <Typography variant="body1" fontWeight={600}>
                        {productionOverview.average_daily_production?.toFixed(0) || '0'} eggs
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Production per Bird
                      </Typography>
                      <Typography variant="body1" fontWeight={600}>
                        {productionOverview.production_per_bird?.toFixed(2) || '0'}
                      </Typography>
                    </Box>
                  </Box>
                </Paper>
              )}

              {/* Mortality Stats */}
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  flex: 1,
                  minWidth: 280,
                  background:
                    'linear-gradient(135deg, rgba(244, 67, 54, 0.05) 0%, rgba(233, 30, 99, 0.05) 100%)',
                  border: '1px solid rgba(244, 67, 54, 0.2)',
                  borderRadius: 2,
                }}
              >
                <Typography variant="subtitle2" fontWeight="bold" color="error.main" mb={2}>
                  MORTALITY OVERVIEW
                </Typography>
                <Box display="flex" flexDirection="column" gap={1.5}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Total Deaths
                    </Typography>
                    <Typography variant="body1" fontWeight={600}>
                      {mortalityOverview.total_mortalities?.toLocaleString() || '0'}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Mortality Rate
                    </Typography>
                    <Typography variant="body1" fontWeight={600} color="error.main">
                      {mortalityOverview.mortality_rate?.toFixed(2) || '0'}%
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Surviving Birds
                    </Typography>
                    <Typography variant="body1" fontWeight={600} color="success.main">
                      {mortalityOverview.surviving_birds?.toLocaleString() || '0'}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Survival Rate
                    </Typography>
                    <Typography variant="body1" fontWeight={600} color="success.main">
                      {mortalityOverview.survival_rate?.toFixed(2) || '0'}%
                    </Typography>
                  </Box>
                </Box>
              </Paper>

              {/* Financial Stats */}
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  flex: 1,
                  minWidth: 280,
                  background:
                    'linear-gradient(135deg, rgba(255, 152, 0, 0.05) 0%, rgba(255, 193, 7, 0.05) 100%)',
                  border: '1px solid rgba(255, 152, 0, 0.2)',
                  borderRadius: 2,
                }}
              >
                <Typography variant="subtitle2" fontWeight="bold" color="secondary.main" mb={2}>
                  FINANCIAL SUMMARY
                </Typography>
                <Box display="flex" flexDirection="column" gap={1.5}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Total Revenue
                    </Typography>
                    <Typography variant="body1" fontWeight={600}>
                      GH₵ {((financialOverview.total_revenue || 0) / 100).toLocaleString()}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Total Expenses
                    </Typography>
                    <Typography variant="body1" fontWeight={600}>
                      GH₵ {((expenditureOverview.total_expenditures || 0) / 100).toLocaleString()}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Net Profit
                    </Typography>
                    <Typography variant="body1" fontWeight={600} color="success.main">
                      GH₵ {((financialOverview.net_profit || 0) / 100).toLocaleString()}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Profit Margin
                    </Typography>
                    <Typography variant="body1" fontWeight={600}>
                      {financialOverview.profit_margin?.toFixed(2) || '0'}%
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      ROI
                    </Typography>
                    <Typography variant="body1" fontWeight={600} color="primary">
                      {financialOverview.roi_percentage?.toFixed(2) || '0'}%
                    </Typography>
                  </Box>
                </Box>
              </Paper>

              {/* Performance Metrics */}
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  flex: 1,
                  minWidth: 280,
                  background:
                    'linear-gradient(135deg, rgba(156, 39, 176, 0.05) 0%, rgba(103, 58, 183, 0.05) 100%)',
                  border: '1px solid rgba(156, 39, 176, 0.2)',
                  borderRadius: 2,
                }}
              >
                <Typography variant="subtitle2" fontWeight="bold" sx={{ color: '#9c27b0' }} mb={2}>
                  PERFORMANCE METRICS
                </Typography>
                <Box display="flex" flexDirection="column" gap={1.5}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Efficiency Score
                    </Typography>
                    <Typography variant="body1" fontWeight={600}>
                      {performanceMetrics.efficiency_score?.toFixed(1) || '0'}/100
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Health Score
                    </Typography>
                    <Typography variant="body1" fontWeight={600}>
                      {performanceMetrics.health_score?.toFixed(1) || '0'}/100
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Productivity Score
                    </Typography>
                    <Typography variant="body1" fontWeight={600}>
                      {performanceMetrics.productivity_score?.toFixed(1) || '0'}/100
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Profitability Score
                    </Typography>
                    <Typography variant="body1" fontWeight={600}>
                      {performanceMetrics.profitability_score?.toFixed(1) || '0'}/100
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            </Box>

            {/* Alerts Section */}
            {alerts && alerts.length > 0 && (
              <Box mt={3}>
                <Typography variant="h6" fontWeight="bold" mb={2}>
                  Alerts & Warnings
                </Typography>
                <Box display="flex" flexDirection="column" gap={1.5}>
                  {alerts.map((alert: any, index: number) => (
                    <Alert
                      key={index}
                      severity={
                        alert.level === 'critical'
                          ? 'error'
                          : alert.level === 'warning'
                            ? 'warning'
                            : 'info'
                      }
                      sx={{ borderRadius: 2 }}
                    >
                      <Typography variant="body2" fontWeight={600}>
                        {alert.message}
                      </Typography>
                    </Alert>
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        </TabPanel>

        {/* Expenses Tab */}
        <TabPanel value={tabValue} index={1}>
          <Box px={2}>
            <Typography variant="h6" fontWeight="bold" mb={3}>
              Recent Expenditure Records
            </Typography>
            {overviewLoading ? (
              <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress />
              </Box>
            ) : (
              <DataGrid
                rows={expenditureOverview?.recent_expenditures || []}
                columns={expenditureColumns}
                initialState={{
                  pagination: {
                    paginationModel: { pageSize: 25, page: 0 },
                  },
                }}
                pageSizeOptions={[10, 25, 50]}
                disableRowSelectionOnClick
                autoHeight
                sx={{
                  border: 0,
                  '& .MuiDataGrid-cell:focus': {
                    outline: 'none',
                  },
                }}
              />
            )}
          </Box>
        </TabPanel>

        {/* Feed Consumption Tab */}
        <TabPanel value={tabValue} index={2}>
          <Box px={2}>
            <Typography variant="h6" fontWeight="bold" mb={3}>
              Feed Consumption History
            </Typography>
            <DataGrid
              rows={feedLogs}
              columns={feedColumns}
              loading={logsLoading}
              initialState={{
                pagination: {
                  paginationModel: { pageSize: 25, page: 0 },
                },
              }}
              pageSizeOptions={[10, 25, 50]}
              disableRowSelectionOnClick
              autoHeight
              sx={{
                border: 0,
                '& .MuiDataGrid-cell:focus': {
                  outline: 'none',
                },
              }}
            />
          </Box>
        </TabPanel>

        {/* Egg Production Tab */}
        <TabPanel value={tabValue} index={3}>
          <Box px={2}>
            <Typography variant="h6" fontWeight="bold" mb={3}>
              Recent Egg Production Records
            </Typography>
            {overviewLoading ? (
              <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress />
              </Box>
            ) : bird.is_laying ? (
              <DataGrid
                rows={productionOverview?.recent_production || []}
                columns={eggProductionColumns}
                loading={overviewLoading}
                initialState={{
                  pagination: {
                    paginationModel: { pageSize: 25, page: 0 },
                  },
                }}
                pageSizeOptions={[10, 25, 50]}
                disableRowSelectionOnClick
                autoHeight
                sx={{
                  border: 0,
                  '& .MuiDataGrid-cell:focus': {
                    outline: 'none',
                  },
                }}
              />
            ) : (
              <Alert severity="info">
                This bird batch is not currently in the laying phase. Egg production data will be
                available once the batch starts laying.
              </Alert>
            )}
          </Box>
        </TabPanel>

        {/* Bird Logs Tab */}
        <TabPanel value={tabValue} index={4}>
          <Box px={2}>
            <Typography variant="h6" fontWeight="bold" mb={3}>
              All Bird Logs (excluding feed)
            </Typography>
            <DataGrid
              rows={nonFeedLogs}
              columns={logsColumns}
              loading={logsLoading}
              initialState={{
                pagination: {
                  paginationModel: { pageSize: 25, page: 0 },
                },
              }}
              pageSizeOptions={[10, 25, 50]}
              disableRowSelectionOnClick
              autoHeight
              sx={{
                border: 0,
                '& .MuiDataGrid-cell:focus': {
                  outline: 'none',
                },
              }}
            />
          </Box>
        </TabPanel>

        {/* Blocks Tab (Rearing or Cage depending on phase) */}
        <TabPanel value={tabValue} index={5}>
          <Box px={2}>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
              <Typography variant="h6" fontWeight="bold">
                {isCaged ? 'Cage Blocks' : 'Rearing Blocks'}
              </Typography>
              <Box display="flex" gap={1}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<ScaleIcon />}
                  onClick={() => navigate(`/birds/${birdId}/weight-trend`)}
                >
                  Weight Trend
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => setAddBlockOpen(true)}
                >
                  Add Block
                </Button>
              </Box>
            </Box>

            {blocksLoading ? (
              <CircularProgress size={24} />
            ) : !visibleBlocks || visibleBlocks.length === 0 ? (
              <Alert severity="info" sx={{ mb: 2 }}>
                No {isCaged ? 'cage' : 'rearing'} blocks yet. Use &quot;Add Block&quot; to add{' '}
                {isCaged ? 'cage rooms' : 'rearing groups'}.
              </Alert>
            ) : (
              <Box display="flex" flexWrap="wrap" gap={2}>
                {visibleBlocks.map((block: BirdBlock) => {
                  const feedInfo = blocksFeed?.blocks?.find(
                    (b: any) => b.block.id === block.id
                  )?.feed;
                  const ageWeek = bird?.age_weeks ?? 0;
                  const qty = block.current_quantity ?? block.initial_quantity ?? 0;
                  const feederRate = ageWeek >= 11 ? 3 : 2;
                  const feedersRequired = qty > 0 ? (qty / 100) * feederRate : 0;
                  const drinkersRequired = qty > 0 ? qty / 125 : 0;
                  const feederShort =
                    block.feeders != null ? Math.max(0, feedersRequired - block.feeders) : 0;
                  const drinkerShort =
                    block.drinkers != null ? Math.max(0, drinkersRequired - block.drinkers) : 0;
                  return (
                    <Paper
                      key={block.id}
                      elevation={0}
                      sx={{
                        p: 2.5,
                        flex: '1 1 280px',
                        minWidth: 260,
                        maxWidth: 360,
                        border: '1px solid rgba(0,0,0,0.08)',
                        borderRadius: 2,
                      }}
                    >
                      <Box display="flex" alignItems="flex-start" justifyContent="space-between">
                        <Box>
                          <Typography variant="subtitle1" fontWeight={700}>
                            {block.name}
                          </Typography>
                          {block.color && (
                            <Chip
                              label={block.color.charAt(0).toUpperCase() + block.color.slice(1)}
                              size="small"
                              sx={{
                                mt: 0.25,
                                height: 18,
                                fontSize: 11,
                                bgcolor:
                                  block.color === 'brown'
                                    ? 'rgba(131,65,5,0.12)'
                                    : 'rgba(200,200,200,0.25)',
                                color: block.color === 'brown' ? '#834105' : 'text.secondary',
                              }}
                            />
                          )}
                        </Box>
                        <Box>
                          <Tooltip title="Edit block">
                            <IconButton size="small" onClick={() => handleOpenEditBlock(block)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete block">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => {
                                if (window.confirm(`Delete block "${block.name}"?`)) {
                                  deleteBlock.mutate(block.id);
                                }
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Box>

                      <Box mt={1.5} display="flex" gap={3}>
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Current
                          </Typography>
                          <Typography variant="h5" fontWeight={700} color="primary">
                            {block.current_quantity}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Initial
                          </Typography>
                          <Typography variant="h5" fontWeight={400} color="text.secondary">
                            {block.initial_quantity}
                          </Typography>
                        </Box>
                      </Box>

                      {(block.feeders != null || block.drinkers != null) && (
                        <Box mt={1} display="flex" gap={2}>
                          {block.feeders != null && (
                            <Box>
                              <Typography variant="caption" color="text.secondary">
                                Feeders
                              </Typography>
                              <Box display="flex" alignItems="baseline" gap={0.75}>
                                <Typography variant="body2" fontWeight={600}>
                                  {block.feeders}
                                </Typography>
                                {feederShort > 0 && (
                                  <Tooltip
                                    title={`Need ${Math.ceil(feedersRequired)} (${feederRate} per 100 birds)`}
                                    arrow
                                  >
                                    <Typography
                                      variant="caption"
                                      color="warning.main"
                                      fontWeight={700}
                                      sx={{ cursor: 'default' }}
                                    >
                                      −{Math.ceil(feederShort)}
                                    </Typography>
                                  </Tooltip>
                                )}
                              </Box>
                            </Box>
                          )}
                          {block.drinkers != null && (
                            <Box>
                              <Typography variant="caption" color="text.secondary">
                                Drinkers
                              </Typography>
                              <Box display="flex" alignItems="baseline" gap={0.75}>
                                <Typography variant="body2" fontWeight={600}>
                                  {block.drinkers}
                                </Typography>
                                {drinkerShort > 0 && (
                                  <Tooltip
                                    title={`Need ${Math.ceil(drinkersRequired)} (1 per 125 birds)`}
                                    arrow
                                  >
                                    <Typography
                                      variant="caption"
                                      color="warning.main"
                                      fontWeight={700}
                                      sx={{ cursor: 'default' }}
                                    >
                                      −{Math.ceil(drinkerShort)}
                                    </Typography>
                                  </Tooltip>
                                )}
                              </Box>
                            </Box>
                          )}
                        </Box>
                      )}

                      {feedInfo && (
                        <>
                          <Divider sx={{ my: 1.5 }} />
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                            mb={0.5}
                          >
                            FEED REQUIREMENT
                          </Typography>
                          <Typography variant="body2" fontWeight={600}>
                            {feedInfo.total_feed_kg} kg/day
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {feedInfo.daily_feed_per_bird_g} g/bird · {feedInfo.bags_required_daily}{' '}
                            bag
                            {feedInfo.bags_required_daily !== 1 ? 's' : ''}
                            {feedInfo.remaining_kg_daily > 0
                              ? ` + ${feedInfo.remaining_kg_daily} kg`
                              : ''}{' '}
                            /day
                          </Typography>
                        </>
                      )}
                    </Paper>
                  );
                })}
              </Box>
            )}

            {/* Aggregate feed summary */}
            {blocksFeed?.aggregate && (
              <Paper
                elevation={0}
                sx={{
                  mt: 3,
                  p: 2,
                  border: '1px solid rgba(131,65,5,0.15)',
                  background:
                    'linear-gradient(135deg, rgba(131,65,5,0.04) 0%, rgba(255,152,0,0.04) 100%)',
                  borderRadius: 2,
                }}
              >
                <Typography variant="subtitle2" fontWeight={700} color="primary" mb={1}>
                  TOTAL FLOCK FEED
                </Typography>
                <Typography variant="body2">
                  <strong>{blocksFeed.aggregate.total_feed_kg} kg/day</strong>
                  {' · '}
                  {blocksFeed.aggregate.bags_required_daily} bag
                  {blocksFeed.aggregate.bags_required_daily !== 1 ? 's' : ''}
                  {blocksFeed.aggregate.remaining_kg_daily > 0
                    ? ` + ${blocksFeed.aggregate.remaining_kg_daily} kg`
                    : ''}{' '}
                  /day
                </Typography>
              </Paper>
            )}
          </Box>
        </TabPanel>
      </Paper>

      {/* Add Block Dialog */}
      <Dialog open={addBlockOpen} onClose={() => setAddBlockOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Block</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            {newBlockRows.map((row, i) => (
              <Paper key={i} variant="outlined" sx={{ p: 1.5, borderRadius: 1.5 }}>
                <Box display="flex" gap={1} alignItems="flex-start" flexWrap="wrap">
                  <TextField
                    label="Block name"
                    size="small"
                    value={row.name}
                    onChange={(e) => {
                      const updated = [...newBlockRows];
                      updated[i] = { ...updated[i], name: e.target.value };
                      setNewBlockRows(updated);
                    }}
                    sx={{ flex: 2, minWidth: 120 }}
                  />
                  <Select
                    size="small"
                    value={row.type}
                    onChange={(e) => {
                      const updated = [...newBlockRows];
                      updated[i] = { ...updated[i], type: e.target.value };
                      setNewBlockRows(updated);
                    }}
                    sx={{ flex: 1, minWidth: 110 }}
                  >
                    <MenuItem value="rearing">Rearing</MenuItem>
                    <MenuItem value="cage">Cage</MenuItem>
                  </Select>
                  <TextField
                    label="Qty"
                    type="number"
                    size="small"
                    value={row.quantity}
                    onChange={(e) => {
                      const updated = [...newBlockRows];
                      updated[i] = { ...updated[i], quantity: e.target.value };
                      setNewBlockRows(updated);
                    }}
                    slotProps={{ htmlInput: { min: 1 } }}
                    sx={{ flex: 1, minWidth: 80 }}
                  />
                  {newBlockRows.length > 1 && (
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => setNewBlockRows(newBlockRows.filter((_, j) => j !== i))}
                      sx={{ mt: 0.5 }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
              </Paper>
            ))}
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={() =>
                setNewBlockRows([...newBlockRows, { name: '', quantity: '', type: blockType }])
              }
            >
              Add another block
            </Button>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddBlockOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleAddBlocksSave}
            disabled={
              createBlocks.isPending ||
              newBlockRows.every((r) => !r.name.trim() || !parseInt(r.quantity))
            }
            sx={{ color: '#fff' }}
          >
            {createBlocks.isPending ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Block Dialog */}
      <Dialog open={editBlockOpen} onClose={() => setEditBlockOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Edit Block</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <TextField
              label="Block name"
              fullWidth
              value={editBlockName}
              onChange={(e) => setEditBlockName(e.target.value)}
            />
            <TextField
              label="Initial bird count"
              type="number"
              fullWidth
              value={editBlockInitialQty}
              onChange={(e) => setEditBlockInitialQty(e.target.value)}
              inputProps={{ min: 1, step: 1 }}
              helperText="Changing this adjusts the initial quantity; current count is recalculated from mortalities"
            />
            <TextField
              label="Notes"
              fullWidth
              multiline
              rows={2}
              value={editBlockNotes}
              onChange={(e) => setEditBlockNotes(e.target.value)}
            />
            <Box display="flex" gap={1.5}>
              <TextField
                label="Feeders"
                type="number"
                size="small"
                value={editBlockFeeders}
                onChange={(e) => setEditBlockFeeders(e.target.value)}
                inputProps={{ min: 0, step: 1 }}
                sx={{ flex: 1 }}
              />
              <TextField
                label="Drinkers"
                type="number"
                size="small"
                value={editBlockDrinkers}
                onChange={(e) => setEditBlockDrinkers(e.target.value)}
                inputProps={{ min: 0, step: 1 }}
                sx={{ flex: 1 }}
              />
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditBlockOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSaveEditBlock}
            disabled={updateBlock.isPending || !editBlockName.trim()}
            sx={{ color: '#fff' }}
          >
            {updateBlock.isPending ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BirdDetailPage;
